import { computed, type MaybeRef, reactive, unref, watch } from 'vue';
import type { Airflow, Dict } from '@aviata/platform-types';
import { type ComposerTranslation, useI18n } from 'vue-i18n';
import { isNil, sumBy, toSafeInteger } from 'lodash-es';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import { dateUtils, type ticketDetails } from 'shared';
import { amplitudeExperiment, flightEntity } from 'freedom-shared';

dayjs.extend(utc);
dayjs.extend(duration);

import {
  type FareFamilyChoiceSpec,
  BaseFareFamilyChoice,
  FareFamilyChoice,
} from './fare-family-choice';

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export function createFareFamily({
  offer,
  t,
}: {
  offer: Airflow.Offer.Details['offer'];
  t: ComposerTranslation;
}): FareFamilyChoiceSpec[] {
  const fareFamilyChoices = offer?.fare_family?.choices || [];

  if (fareFamilyChoices.length > 0) {
    return fareFamilyChoices.map(choice => new FareFamilyChoice(choice));
  }

  return [new BaseFareFamilyChoice(offer, t)];
}

function isFareFamilyFlexibleOffer(
  offer: Airflow.Offer.Details['offer'],
): offer is WithRequired<
  Airflow.Offer.Details['offer'],
  'fare_families_flexible'
> {
  return 'fare_families_flexible' in offer;
}

function getGroupKey(segments: number[]) {
  return `group-${segments.join(':')}`;
}

function getFlexibleFareGroups(
  offer: WithRequired<Airflow.Offer.Details['offer'], 'fare_families_flexible'>,
) {
  return offer.fare_families_flexible.reduce(
    (
      acc: Record<string, FareFamilyChoiceSpec[]>,
      group: { segments: number[]; choices: Airflow.FareFamily.Choice[] },
    ) => {
      acc[getGroupKey(group.segments)] = group.choices.map(
        choice => new FareFamilyChoice(choice),
      );
      return acc;
    },
    {},
  );
}

function getBasicFareGroups(
  offer: Airflow.Offer.Details['offer'],
  { t }: { t: ComposerTranslation },
) {
  const segments = offer.flights.flatMap(flight => flight.segments) || [];
  const segmentIds = Array.from({ length: segments.length }).map((_, i) => i);

  return {
    [getGroupKey(segmentIds)]: !isNil(offer)
      ? createFareFamily({ offer, t })
      : [],
  };
}

export function useFareFamily({
  offer,
  dict,
}: {
  offer: MaybeRef<Airflow.Offer.Details['offer'] | undefined>;
  dict: MaybeRef<Dict.Entity | null>;
}) {
  const _offer = computed(() => unref(offer));
  const _dict = computed(() => unref(dict));

  const { isVariant: isFareFamiliesFlexibleEnabled } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.FARE_FAMILIES_FLEXIBLE,
      'b',
    );

  const { t } = useI18n();

  /**
   * @deprecated Will be deprecated after a wile because flexible fares will cover base fares from backend
   */
  const isBaseFareFamily = computed(
    () =>
      !isNil(_offer.value) &&
      isNil(_offer.value?.fare_family) &&
      isNil(_offer.value?.fare_families_flexible) &&
      !isFareFamiliesFlexibleEnabled.value,
  );

  const segments = computed(
    () => _offer.value?.flights.flatMap(flight => flight.segments) || [],
  );

  const flightGroups = computed(() => {
    if (
      isNil(_offer.value) ||
      isNil(_dict.value) ||
      !isFareFamilyFlexibleOffer(_offer.value) ||
      !isFareFamiliesFlexibleEnabled.value
    ) {
      return {};
    }

    return _offer.value.fare_families_flexible.reduce(
      (acc: Record<string, ticketDetails.types.MappedFlight>, group) => {
        const groupSegments = group.segments.map(
          segment => segments.value[segment],
        );

        const firstSegment = groupSegments[0].dep.at;
        const lastSegment = groupSegments[groupSegments.length - 1].arr.at;

        const departure = dayjs.utc(firstSegment);
        const arrival = dayjs.utc(lastSegment);

        const diffMs = arrival.diff(departure, 'second');

        acc[getGroupKey(group.segments)] = {
          duration: diffMs,
          durationText: dateUtils.prettyDurationFromSeconds(diffMs, true),
          segments: groupSegments.map(segment =>
            // @ts-expect-error platform-types version mismatch
            flightEntity.lib.mapSegment(_dict.value, segment),
          ),
        };
        return acc;
      },
      {},
    );
  });

  const fareGroups = computed(() => {
    if (isNil(_offer.value)) {
      return {};
    }

    return isFareFamilyFlexibleOffer(_offer.value) &&
      isFareFamiliesFlexibleEnabled.value
      ? getFlexibleFareGroups(_offer.value)
      : getBasicFareGroups(_offer.value, { t });
  });

  const selectedFares = reactive<Record<string, FareFamilyChoiceSpec>>({});

  function selectFare({
    groupKey,
    choice,
  }: {
    groupKey: string;
    choice: FareFamilyChoiceSpec;
  }) {
    selectedFares[groupKey] = choice;
  }

  watch(_offer, () => {
    if (
      isFareFamiliesFlexibleEnabled.value &&
      !isNil(_offer.value?.fare_families_flexible)
    ) {
      _offer.value?.fare_families_flexible.forEach(fareFamily => {
        if (fareFamily.choices.length === 1) {
          selectFare({
            groupKey: getGroupKey(fareFamily.segments),
            choice: new FareFamilyChoice(fareFamily.choices[0]),
          });
        }
      });
    }
  });

  function prepareFarePayload() {
    return Object.entries(selectedFares)
      .sort(([keyA], [keyB]) => {
        return keyA.localeCompare(keyB, undefined, { numeric: true });
      })
      .map(([_, value]) => value.id)
      .join(':');
  }

  const ticketPrice = computed(() => {
    return sumBy(Object.values(selectedFares), fare =>
      toSafeInteger(fare.price.amount),
    );
  });

  return {
    ticketPrice,
    flightGroups,
    fareGroups,
    selectedFares,
    selectFare,
    prepareFarePayload,
    isBaseFareFamily,
  };
}

export type { FareFamilyChoiceSpec };
